import { CurrencyHelpers } from '@/helpers/utils/currencyHelpers';
import { formatAmount, formatCurrency, formatDateMoment, formatMobileNumber } from '@/helpers/utils/siteUtils';
import { DATE_FORMAT, DATE_TIME_FORMAT } from '../../../../../constants/constant';
import {
  CREDIT_SUMMARY_LABELS,
  CUSTOMER_DETAILS_HEADER_KEYS,
  DEFAULT_COUNTRY,
  Hold_Order,
  cardRegex,
} from '../constants';
import {
  AddressInfo,
  CatalogMailHistory,
  CatalogMailResponse,
  Customer,
  CustomerDetailsHeaderItem,
  CustomerDetailsResponse,
  CustomerDetailsType,
  CustomerFormData,
  DivisionCustomer,
  FormattedNote,
  HoldOrder,
  Image,
  Note,
  Order,
  OrderState,
  Preference,
  SegregatedPreferences,
  ShippingAddressDetailsType,
  TrackingInfo,
  TransformedOrder,
} from '../types';

export const getFormData = (data: CustomerDetailsType) => {
  const extractedData = {
    firstName: data?.CustomerInfo?.firstName,
    lastName: data?.CustomerInfo?.lastName,
    companyName: data?.CustomerInfo?.companyName,
    addressLine1: data?.BillingAddress?.LocationInfo?.line1,
    additionalAddressInfo: data?.BillingAddress?.LocationInfo?.line2,
    city: data?.BillingAddress?.LocationInfo?.city,
    state: data?.BillingAddress?.LocationInfo?.state,
    zip: data?.BillingAddress?.LocationInfo?.zip,
    phone: formatMobileNumber(data?.DivisionCustomers?.[0]?.PhoneRecords?.[0]?.PhoneInfo?.phoneNumber || ''),
    emailAddress: data?.DivisionCustomers?.[0]?.EmailRecords?.[0]?.EmailInfo?.emailAddress || '',
    dob: data?.CustomerInfo?.dob || '',
    ssn: data?.CustomerInfo?.ssn || '',
    mailReturnUndeliverable: data?.BillingAddress?.LocationInfo?.mailWasReturned ?? true,
  };

  return extractedData;
};

export function getCustomerDetails(data: CustomerDetailsType) {
  const accountData = [
    { field: CUSTOMER_DETAILS_HEADER_KEYS.ACCOUNT_NUMBER, value: data?.CustomerInfo?.omsCustomerId, mask: true },
    {
      field: CUSTOMER_DETAILS_HEADER_KEYS.FIRST_NAME,
      value: data?.CustomerInfo?.firstName,
      key: 'firstName',
      mask: true,
    },
    { field: CUSTOMER_DETAILS_HEADER_KEYS.LAST_NAME, value: data?.CustomerInfo?.lastName, key: 'lastName', mask: true },
    {
      field: CUSTOMER_DETAILS_HEADER_KEYS.COMPANY_NAME,
      value: data?.CustomerInfo?.companyName,
      key: 'companyName',
      mask: true,
    },
    {
      field: CUSTOMER_DETAILS_HEADER_KEYS.PHONE_NUMBER,
      value: data?.DivisionCustomers?.length > 0 ? data?.DivisionCustomers?.[0]?.PhoneRecords : [],
      key: 'phoneNumber',
      mask: true,
    },
    {
      field: CUSTOMER_DETAILS_HEADER_KEYS.EMAIL_ADDRESS,
      value: data?.DivisionCustomers?.length > 0 ? data?.DivisionCustomers?.[0]?.EmailRecords : [],
      key: 'email',
      mask: true,
    },
    {
      field: CUSTOMER_DETAILS_HEADER_KEYS.DATE_OF_BIRTH,
      value: data?.CustomerInfo?.dob || '',
      key: 'dob',
      mask: true,
    },
    {
      field: CUSTOMER_DETAILS_HEADER_KEYS.SOCIAL_SECURITY_NUMBER,
      value: data?.CustomerInfo?.ssn || '',
      key: 'ssn',
      mask: true,
    },
  ];
  const accountAddress = [
    {
      field: CUSTOMER_DETAILS_HEADER_KEYS.ADDRESS_LINE_1,
      value: data?.BillingAddress?.LocationInfo?.line1,
      key: 'streetName',
      mask: true,
    },
    {
      field: CUSTOMER_DETAILS_HEADER_KEYS.ADDRESS_LINE_2,
      value: data?.BillingAddress?.LocationInfo?.line2 || '',
      key: 'additionalAddressInfo',
      mask: true,
    },
    {
      field: CUSTOMER_DETAILS_HEADER_KEYS.CITY,
      value: data?.BillingAddress?.LocationInfo?.city,
      key: 'city',
      mask: true,
    },
    {
      field: CUSTOMER_DETAILS_HEADER_KEYS.STATE,
      value: data?.BillingAddress?.LocationInfo?.state,
      key: 'state',
      mask: true,
    },
    {
      field: CUSTOMER_DETAILS_HEADER_KEYS.ZIP_CODE,
      value: data?.BillingAddress?.LocationInfo?.zip,
      key: 'postalCode',
      mask: true,
    },
    {
      field: CUSTOMER_DETAILS_HEADER_KEYS.WEB_ACCOUNT_NUMBER,
      value: data?.DivisionCustomers?.length > 0 ? data?.DivisionCustomers?.[0]?.WebAccounts?.[0]?.webAccountId : '',
      key: 'webAccountId',
      mask: true,
    },
    {
      field: CUSTOMER_DETAILS_HEADER_KEYS.DIVISION_ID,
      value: data?.DivisionCustomers?.length > 0 ? data?.DivisionCustomers?.[0]?.divisionCustomerId : '',
      key: 'divisionId',
      mask: true,
    },
    {
      field: CUSTOMER_DETAILS_HEADER_KEYS.MAIL_RETURN_UNDELIVERABLE,
      value: data?.BillingAddress?.LocationInfo?.mailWasReturned,
      key: 'mailReturnUndeliverable',
      mask: true,
    },
  ];

  return { accountData, accountAddress };
}

export function transformNotes(notes?: Note[]): FormattedNote[] {
  return (
    notes?.map((note) => {
      const { note: noteText, updatedBy, updatedOn } = note;
      const [agentName, dateTime] =
        updatedBy && updatedOn ? [`${updatedBy}`, formatDateMoment(updatedOn, DATE_TIME_FORMAT)] : ['', ''];
      return { agent: agentName, dateTime, notes: noteText };
    }) ?? []
  );
}
export function transformOrderData(orderData: Order[]): TransformedOrder[] {
  if (!orderData || orderData?.length === 0) {
    return [];
  }

  return orderData?.map((order) => {
    const { orderId } = order;
    const orderInfo = order?.OrderInfo ?? {};
    const { submittedOn, medium, updatedOn, statusDescription, divisionCode } = orderInfo;
    const orderDate = submittedOn ? formatDateMoment(submittedOn, DATE_FORMAT) : '';
    const state: OrderState = { statusDescriptions: statusDescription, trackingDetails: [] };
    const status: TrackingInfo[] = [];
    const recipients = order?.Recipients?.[0] ?? [];
    const shipments = recipients?.Shipments ?? [];

    shipments?.forEach((items) => {
      const trackingDetails = items?.TrackingDetails ?? [];
      trackingDetails?.forEach((data: any) => {
        const info = data?.TrackingInfo ?? {};
        status.push({
          trackingCode: info?.trackingCode,
          trackingLink: info?.trackingLink,
          externalTrackingLink: info?.externalTrackingLink,
        });
      });
    });

    state.trackingDetails = status;
    const lineItems = recipients?.LineItems ?? [];
    const numItems = lineItems?.length;
    const payments = order?.Payments ?? [];
    let paymentType = payments[0]?.PaymentInfo?.paymentType ?? payments[0]?.PaymentInfo?.termsDescription ?? 'N/A';
    if (
      divisionCode === 'ZD' &&
      (paymentType === 'MasonCredit' || paymentType === 'Easy Pay Payment Option' || paymentType === 'Mason Credit')
    ) {
      paymentType = 'Mason Easy-Pay';
    }
    const pricingInfo = (order?.PricingInfo as any) ?? {};
    const totalAmount = formatCurrency(pricingInfo?.EstimatedTotal?.totalAmt?.toString()) ?? '0';
    const imageUrls: Image[] = lineItems
      .filter((lineItem) => !lineItem?.LineItemInfo?.isShippingInsurancePolicy)
      ?.map((item) => {
        const image = item?.imageData?.[0]?.value;
        const fallback = !image && !item?.imageUrl;
        return {
          imageUrl: image ?? item?.imageUrl ?? 'noImage',
          isFallback: fallback,
        };
      })
      .filter((item) => item?.imageUrl)
      .sort((a, b) => {
        if (a.isFallback && !b.isFallback) return 1;
        if (!a.isFallback && b.isFallback) return -1;
        return 0;
      });

    return {
      orderId,
      submittedOn: orderDate,
      statusDescription: state,
      totalAmount,
      totalItems: numItems,
      paymentMethod: paymentType,
      origin: medium,
      lastModifiedOn: updatedOn ?? '',
      imageData: imageUrls,
    };
  });
}

export function transformCreditSummaryData(data: DivisionCustomer) {
  if (!data) {
    return [];
  }

  const divisionCustomerInfo = data.DivisionCustomerInfo || {};
  const { creditLimit, softCreditLimitExpirationDate } = divisionCustomerInfo;

  const creditAccount = data.CreditAccount || {};
  const accountInfo = creditAccount.AccountInfo || {};
  const {
    accountBalance,
    availableCredit,
    monthlyPaymentAmt,
    accountStatusDisplay,
    nextPaymentOn,
    minimumPaymentDue,
    lastStatementOn,
    nextStatementOn,
    amountPastDue,
    interestRate,
  } = accountInfo;

  const digitalPrequalInfo = data?.DigitalPrequalInfo || {};
  const { lastPrequalAttemptStatus } = digitalPrequalInfo;
  const creditSummaryData = [
    {
      label: CREDIT_SUMMARY_LABELS.CREDIT_LIMIT,
      value: formatAmount(creditLimit ?? ''),
    },
    {
      label: CREDIT_SUMMARY_LABELS.TOTAL_BALANCE,
      value: formatAmount(accountBalance ?? ''),
    },
    {
      label: CREDIT_SUMMARY_LABELS.AVAILABLE_CREDIT,
      value: formatAmount(availableCredit ?? ''),
    },
    {
      label: CREDIT_SUMMARY_LABELS.MONTHLY_PAYMENT,
      value: formatAmount(monthlyPaymentAmt ?? ''),
    },
    {
      label: CREDIT_SUMMARY_LABELS.ACCOUNT_STATUS,
      value: accountStatusDisplay ?? '',
    },
    {
      label: CREDIT_SUMMARY_LABELS.PAYMENT_DUE_DATE,
      value: formatDateMoment(nextPaymentOn ?? '', DATE_FORMAT),
    },
    {
      label: CREDIT_SUMMARY_LABELS.MIN_PAYMENT_DUE,
      value: formatAmount(minimumPaymentDue ?? ''),
    },
    {
      label: CREDIT_SUMMARY_LABELS.LAST_STATEMENT_DATE,
      value: formatDateMoment(lastStatementOn ?? '', DATE_FORMAT),
    },
    {
      label: CREDIT_SUMMARY_LABELS.NEXT_STATEMENT_DATE,
      value: formatDateMoment(nextStatementOn ?? '', DATE_FORMAT),
    },
    {
      label: CREDIT_SUMMARY_LABELS.AMOUNT_PAST_DUE,
      value: formatAmount(amountPastDue ?? '') ?? '',
    },
    {
      label: CREDIT_SUMMARY_LABELS.INTEREST_RATE,
      value: interestRate ? `${interestRate ?? 0.0}%` : '',
    },
    {
      label: CREDIT_SUMMARY_LABELS.INTERNET_CREDIT_OFFER,
      value: '',
    },
    {
      label: CREDIT_SUMMARY_LABELS.PREQUAL_EXP_DATE,
      value: formatDateMoment(softCreditLimitExpirationDate ?? '', DATE_FORMAT) ?? '',
    },
    {
      label: CREDIT_SUMMARY_LABELS.LAST_PREQUAL_STATUS,
      value: lastPrequalAttemptStatus ?? '',
    },
  ];
  return creditSummaryData;
}

export function segregatePreferences(preferences: Preference[]): SegregatedPreferences {
  const masterLevelPreferences: Preference[] = [];
  const divisionLevelPreferences: Preference[] = [];

  preferences.forEach((pref) => {
    if (pref.name.startsWith('MAS-')) {
      masterLevelPreferences.push(pref);
    } else {
      divisionLevelPreferences.push(pref);
    }
  });

  return {
    masterLevelPreferences,
    divisionLevelPreferences,
  };
}

export const transformRecipientAddress = (address: any) => ({
  fullName: `${address?.PersonInfo?.firstName ?? ''} ${address?.PersonInfo?.lastName ?? ''}`.trim(),
  addressId: address?.HeaderInfo?.addressId ?? '',
  firstName: address?.PersonInfo?.firstName ?? '',
  lastName: address?.PersonInfo?.lastName ?? '',
  streetName: address?.LocationInfo?.line1 ?? '',
  city: address?.LocationInfo?.city ?? '',
  country: address?.LocationInfo?.country ?? '',
  state: address?.LocationInfo?.state ?? '',
  postalCode: address?.LocationInfo?.zip ?? '',
  additionalAddressInfo: address?.LocationInfo?.line2 ?? '',
  phone: address?.LocationInfo?.phoneNumber ?? '',
});

export function transformShippingAddress(data: AddressInfo[]) {
  if (data?.length === 0 || data === undefined) {
    return [];
  }
  const shippingAddress = [] as ShippingAddressDetailsType[];
  data?.forEach((item) => {
    const headerInfo = item.HeaderInfo || {};
    const { addressId } = headerInfo;

    const personInfo = item.PersonInfo || {};
    const { firstName, lastName, companyName } = personInfo;

    const locationInfo = item.LocationInfo || {};
    const { line1, city, country, state, zip, phoneNumber, line2 } = locationInfo;

    shippingAddress.push({
      fullName: `${firstName} ${lastName}`,
      addressId,
      firstName,
      lastName,
      streetName: line1,
      city,
      country,
      state,
      postalCode: zip,
      companyName,
      additionalAddressInfo: line2,
      phone: phoneNumber,
    });
  });

  return shippingAddress;
}

export const handlePostalCodeChange = (event: any, setPostalCode: any) => {
  const inputValue = event.target.value.replace(/\D/g, '');
  let maskedValue = '';

  if (inputValue.length <= 5) {
    maskedValue = inputValue;
  } else {
    maskedValue = inputValue.slice(0, 5) + '-' + inputValue.slice(5, 9);
  }
  setPostalCode(maskedValue);
};

export const transformMailHistory = (mailHistoryData: CatalogMailResponse) => {
  if (mailHistoryData === undefined || !mailHistoryData) return [] as CatalogMailHistory[];
  const { lastCatalogRequestedOn } = mailHistoryData;
  const extractedData = mailHistoryData?.MailingRecords?.map((record) => ({
    mailingRecordId: record?.mailingRecordId,
    mailedOn: formatDateMoment(record?.mailedOn, DATE_FORMAT),
    orderPromotionCode: record?.orderPromotionCode ?? '',
    offerExpiresOn: formatDateMoment(record?.offerExpiresOn, DATE_FORMAT),
    lastCatalogRequestedOn: formatDateMoment(lastCatalogRequestedOn, DATE_FORMAT),
  }));

  return extractedData as CatalogMailHistory[];
};

export const getPaymentAmount = (customerDetails: any) => {
  if (!customerDetails) return {};
  const creditAccount = customerDetails?.DivisionCustomers?.[0]?.CreditAccount?.AccountInfo ?? {};
  const { accountBalance, minimumPaymentDue } = creditAccount;
  return {
    accountBalance,
    minimumPaymentDue,
  };
};

export function getCardType(cardNumber: string) {
  const cleanedCardNumber = cardNumber?.replace(/\D/g, '');

  for (const [type, regex] of Object.entries(cardRegex)) {
    if (regex.test(cleanedCardNumber)) {
      return type;
    }
  }
  return 'unknown';
}

export const handleCardNumberChange = (event: any, setCardNumber: any) => {
  const inputValue = event.target.value?.replace(/\D/g, '');
  const chunks = [0, 4, 8, 12];
  const maskedValue = chunks
    .reduce((acc, curr, i) => {
      const chunk = inputValue.slice(curr, chunks[i + 1]);
      return acc.concat(chunk, i < chunks.length - 1 ? ' ' : '');
    }, '')
    .trim();

  setCardNumber(maskedValue);
};

export const getBillingAddressInfo = (customerData: Customer) => {
  if (!customerData) return {} as ShippingAddressDetailsType;

  const LocationInfo = customerData?.BillingAddress?.LocationInfo ?? {};
  const { firstName, lastName, companyName } = customerData.CustomerInfo;
  const phoneNumber = customerData.DivisionCustomers?.[0]?.PhoneRecords?.[0]?.PhoneInfo?.phoneNumber;
  return {
    fullName: `${firstName} ${lastName}`,
    firstName,
    lastName,
    streetName: LocationInfo?.line1,
    additionalAddressInfo: LocationInfo?.line2,
    city: LocationInfo?.city,
    state: LocationInfo?.state,
    postalCode: LocationInfo?.zip,
    country: DEFAULT_COUNTRY,
    addressId: 'B-ADD',
    phone: phoneNumber,
    companyName,
  };
};
export const getBillingAddress = (customerData: any) => {
  const customer = customerData?.Customer;
  if (customer?.CustomerInfo) {
    const LocationInfo = customer?.BillingAddress?.LocationInfo ?? {};
    const { firstName, lastName, companyName } = customer?.CustomerInfo ?? {};
    const phoneNumber = customer.DivisionCustomers?.[0]?.PhoneRecords?.[0]?.PhoneInfo?.phoneNumber;
    return {
      fullName: `${firstName} ${lastName}`,
      firstName,
      lastName,
      streetName: LocationInfo?.line1,
      additionalAddressInfo: LocationInfo?.line2,
      city: LocationInfo?.city,
      state: LocationInfo?.state,
      postalCode: LocationInfo?.zip,
      country: DEFAULT_COUNTRY,
      addressId: 'B-ADD',
      phone: phoneNumber,
      companyName,
    };
  }
};
export const transformOrderHistoryData = (data: any) => {
  const orderData = data?.Orders ?? [];
  const totalOrderHistoryRecords = data?.ResultMetaData?.numberOfRecords ?? 0;
  const transformedOrderData = transformOrderData(orderData || ([] as Order[]));
  const showOrderPagination = data?.ResultMetaData?.hasMoreOrders;
  const nextOrderDate = data?.ResultMetaData?.nextOrderDate;

  return {
    orderData: transformedOrderData,
    totalOrderHistoryRecords,
    showOrderPagination,
    nextOrderDate,
  };
};

export const transformCustomerDetailsData = (data: CustomerDetailsResponse) => {
  const customer = data?.customerDetails?.Customer;
  const orderData = data?.orderHistory?.Orders ?? [];
  const { accountData, accountAddress } = getCustomerDetails(customer);
  const totalOrderHistoryRecords = data?.orderHistory?.ResultMetaData?.numberOfRecords ?? 0;
  const divisionCustomer = customer?.DivisionCustomers?.[0];
  const emailRecords = divisionCustomer?.EmailRecords ?? [];
  const phoneRecords = divisionCustomer?.PhoneRecords ?? [];
  const formData = getFormData(customer);
  const transformedOrderData = transformOrderData(orderData || ([] as Order[]));
  const creditTransactions = data?.creditHistory?.CreditTransactions;
  const shippingAddresses = data?.shippingAddresses?.RecipientAddresses;
  const isEligible = divisionCustomer?.CreditAccount?.CreditProtectionInfo?.isEligible ?? false;
  const creditSummary = transformCreditSummaryData(divisionCustomer);
  const divisionCustomerId = divisionCustomer?.divisionCustomerId ?? '';
  const billingAddress = getBillingAddressInfo(customer);
  const creditAccount = divisionCustomer?.CreditAccount;
  const creditAccountId = creditAccount?.creditAccountId;
  const webAccount = divisionCustomer?.WebAccounts?.[0];
  const webAccountId = webAccount?.webAccountId;
  const firstEmailRecord = emailRecords?.[0];
  const primaryEmail = firstEmailRecord?.EmailInfo?.emailAddress?.trim();
  const creditProtectionInfo = creditAccount?.CreditProtectionInfo;
  const isEnrolled = creditProtectionInfo?.isEnrolled ?? false;
  const customerId = customer?.customerId ?? '';
  const omsCustomerId = customer?.CustomerInfo?.omsCustomerId;
  const divisionCode = divisionCustomer?.divisionCode;
  const lastCatalogRequestedOn = formatDateMoment(
    customer?.DivisionCustomers?.[0]?.DivisionCustomerInfo?.lastCatalogMailedOn,
    DATE_FORMAT,
  );
  const showOrderPagination = data?.orderHistory?.ResultMetaData?.hasMoreOrders;
  const nextOrderDate = data?.orderHistory?.ResultMetaData?.nextOrderDate;
  const dob = customer?.CustomerInfo?.dob ?? '';
  const ssn = customer?.CustomerInfo?.ssn ?? '';
  const billingZipCode = billingAddress?.postalCode?.substring(0, 5);

  return {
    accountAddress,
    accountData,
    emailRecords,
    phoneRecords,
    formData,
    orderData: transformedOrderData,
    totalOrderHistoryRecords,
    creditTransactions,
    shippingAddresses,
    customer,
    isEligible,
    creditSummary,
    divisionCustomerId,
    billingAddress,
    creditAccountId,
    webAccountId,
    primaryEmail,
    creditProtectionInfo,
    isEnrolled,
    customerId,
    omsCustomerId,
    divisionCode,
    lastCatalogRequestedOn,
    showOrderPagination,
    nextOrderDate,
    dob,
    ssn,
    creditAccount,
    billingZipCode,
  };
};

export const updatePhoneNumber = (id: string, newPhoneNumber: string, data: any) => {
  const updatedData = data?.map((item: any) => {
    if (item.field === 'Primary Phone') {
      const updatedPhoneInfo = item?.value?.map((phoneInfo: any) => {
        if (phoneInfo.HeaderInfo.phoneRecordId === id) {
          return {
            ...phoneInfo,
            PhoneInfo: {
              ...phoneInfo.PhoneInfo,
              phoneNumber: newPhoneNumber,
            },
          };
        }
        return phoneInfo;
      });
      return { ...item, value: updatedPhoneInfo };
    }
    return item;
  });
  return updatedData;
};

export const updateEmailAddress = (id: string, newEmail: string, data: any) => {
  const updatedData = data?.map((item: any) => {
    if (item.field === 'Email Address') {
      const updatedEmailInfo = item?.value?.map((emailInfo: any) => {
        if (emailInfo.HeaderInfo.emailRecordId === id) {
          return {
            ...emailInfo,
            EmailInfo: {
              ...emailInfo.EmailInfo,
              emailAddress: newEmail,
            },
          };
        }
        return emailInfo;
      });
      return { ...item, value: updatedEmailInfo };
    }
    return item;
  });
  return updatedData;
};

export const ExtractFormData = (
  accountData: CustomerDetailsHeaderItem[],
  accountAddress: CustomerDetailsHeaderItem[],
) => {
  if (!accountData && !accountAddress) return {} as any;

  const extractedData = {
    firstName: accountData?.find((item) => item.key === 'firstName')?.value ?? '',
    lastName: accountData?.find((item) => item.key === 'lastName')?.value ?? '',
    companyName: accountData?.find((item) => item.key === 'companyName')?.value ?? '',
    streetName: accountAddress?.find((item) => item.key === 'streetName')?.value ?? '',
    additionalAddressInfo: accountAddress?.find((item) => item.key === 'additionalAddressInfo')?.value ?? '',
    city: accountAddress?.find((item) => item.key === 'city')?.value ?? '',
    state: accountAddress?.find((item) => item.key === 'state')?.value ?? '',
    postalCode: accountAddress?.find((item) => item.key === 'postalCode')?.value ?? '',
    phone: formatMobileNumber(
      accountData?.find((item) => item.key === 'phoneNumber')?.value?.[0]?.PhoneInfo?.phoneNumber ?? '',
    ),
    emailAddress: accountData?.find((item) => item.key === 'email')?.value?.[0]?.EmailInfo?.emailAddress ?? '',
    dob: accountData?.find((item) => item.key === 'dob')?.value ?? '',
    ssn: accountData?.find((item) => item.key === 'ssn')?.value ?? '',
    mailReturnUndeliverable: accountAddress?.find((item) => item.key === 'mailReturnUndeliverable')?.value,
  };
  return extractedData;
};

export const isAddressDeliverable = (enteredAddress: CustomerFormData, suggestedAddress: any) => {
  if (!suggestedAddress || Object.keys(suggestedAddress).length === 0) return true;
  const addressFields = ['city', 'postalCode', 'state', 'streetName'];

  for (const field of addressFields) {
    if (suggestedAddress[field] !== enteredAddress[field]) {
      return false;
    }
  }

  return true;
};

export const transformHoldOrders = (data: HoldOrder[]) => {
  return data?.map((Items: any) => {
    const imageData = Items?.lineItems
      ?.map((items: any) => {
        return { imageUrl: items?.variant?.attributesRaw?.[0]?.imageUrl };
      })
      ?.filter((items: any) => items?.imageUrl);
    return {
      id: Items?.id,
      statusDescription: { statusDescriptions: Hold_Order, trackingDetails: [] },
      submittedOn: formatDateMoment(Items?.lastModifiedAt ?? '', DATE_FORMAT) ?? '',
      totalAmount: CurrencyHelpers.customCurrencyFormat(Items?.totalPrice ?? { centAmount: 0 }),
      orderId: Items?.id?.split('-')?.[0],
      totalItems: Items?.lineItems?.length ?? '',
      paymentMethod: getPaymentMethods(
        JSON.parse(Items?.custom?.paymentDetails?.[0]?.value ?? '{}')?.paymentMethod ?? '',
      ),
      origin: getHoldOrderOrigin(Items?.custom?.orderType?.[0]?.value ?? ''),
      lastModifiedOn: formatDateMoment(Items?.lastModifiedAt ?? '', DATE_TIME_FORMAT) ?? '',
      imageData: imageData,
    };
  });
};

export function mapAddressData(data: any) {
  return {
    HeaderInfo: {
      addressId: data?.addressId ?? '',
    },
    PersonInfo: {
      firstName: data?.firstName ?? '',
      lastName: data?.lastName ?? '',
      companyName: data?.companyName ?? '',
    },
    LocationInfo: {
      zip: data?.postalCode ?? '',
      country: data?.country ?? '',
      phoneNumber: data?.phone ?? '',
      city: data?.city ?? '',
      state: data?.state ?? '',
      line1: data?.streetName ?? '',
      line2: data?.additionalAddressInfo ?? '',
    },
  };
}

export const getHoldOrderOrigin = (origin: string) => {
  switch (origin) {
    case 'newOrder':
      return 'New Order';
    case 'mailOrder':
      return 'Mail Order';
    case 'noCharge':
      return 'No Charge';
    default:
      return '';
  }
};

export const getPaymentMethods = (paymentMethod: string) => {
  switch (paymentMethod) {
    case 'CARD':
      return 'Card';
    case 'CREDIT':
      return 'Mason Credit';
    case 'CASH':
      return 'Cash';
    case 'CHECK':
      return 'Check';
    default:
      return '';
  }
};

export const getAvailableCredit = (soaCustomer: Customer | undefined): any => {
  const divisionCustomer = soaCustomer?.DivisionCustomers?.[0];
  let customerAvailableCredit;
  if (divisionCustomer?.CreditAccount) {
    customerAvailableCredit = Number(divisionCustomer.CreditAccount.AccountInfo?.availableCredit ?? 0);
  }
  return customerAvailableCredit;
};
